import React from "react";
import GroupCoverPositioner from "../../components/Groups/GroupCoverPositioner";
import { WAY_OF_FISHING_OPTIONS } from "../../utils/catchMetadataOptions";
import {
  GROUP_POSTING_APPROVAL_MODE_OPTIONS,
  GROUP_WATER_TYPE_OPTIONS,
  getGroupTypeOfFishingLabel,
} from "./GroupsSharedUi";

export default function GroupCreateForm({ controller, t }) {
  return (
    <form className="groups-create-card" onSubmit={controller.handleCreateGroup}>
      <div className="groups-form-row">
        <label htmlFor="group-name">{t("groups_name")}</label>
        <input
          id="group-name"
          type="text"
          value={controller.name}
          onChange={(event) => controller.setName(event.target.value)}
          maxLength={80}
          placeholder={t("groups_name_placeholder")}
        />
      </div>

      <div className="groups-form-row">
        <label htmlFor="group-description">{t("groups_description")}</label>
        <textarea
          id="group-description"
          value={controller.description}
          onChange={(event) => controller.setDescription(event.target.value)}
          maxLength={1000}
          placeholder={t("groups_description_placeholder")}
        />
      </div>

      <div className="groups-form-row">
        <label htmlFor="group-rules">
          {t("groups_rules_label", { defaultValue: "Group rules" })}
        </label>
        <textarea
          id="group-rules"
          value={controller.groupRules}
          onChange={(event) => controller.setGroupRules(event.target.value)}
          maxLength={4000}
          placeholder={t("groups_rules_placeholder", {
            defaultValue: "Share any group rules or posting expectations.",
          })}
        />
      </div>

      <div className="groups-form-row">
        <label htmlFor="group-cover">{t("groups_cover")}</label>
        <div className="groups-cover-upload">
          <input
            ref={controller.groupCoverInputRef}
            id="group-cover"
            type="file"
            accept="image/*"
            className="groups-cover-input"
            onChange={controller.handleGroupCoverChange}
          />
          <div className="groups-cover-upload-actions">
            <button
              type="button"
              className="groups-secondary-btn"
              onClick={() => controller.groupCoverInputRef.current?.click()}
            >
              {controller.groupCoverFile
                ? t("groups_change_cover_image")
                : t("groups_add_cover_image")}
            </button>
            {controller.groupCoverFile ? (
              <button
                type="button"
                className="groups-secondary-btn groups-cover-remove-btn"
                onClick={controller.clearGroupCover}
              >
                {t("groups_remove_cover_image")}
              </button>
            ) : null}
          </div>
          {controller.groupCoverPreviewUrl ? (
            <GroupCoverPositioner
              imageSrc={controller.groupCoverPreviewUrl}
              alt={t("groups_cover_preview_alt")}
              positionX={controller.groupCoverPosition.x}
              positionY={controller.groupCoverPosition.y}
              scale={controller.groupCoverPosition.scale}
              hintLabel={t("drag_to_reposition")}
              onPositionChange={controller.setGroupCoverPosition}
            />
          ) : null}
        </div>
      </div>

      <div className="groups-form-row">
        <div className="groups-form-label-row">
          <label htmlFor="group-type">{t("groups_type")}</label>
          <div className="groups-form-help" ref={controller.groupTypeHelpRef}>
            <button
              type="button"
              className="groups-form-help-trigger"
              aria-label={t("groups_type_help_button")}
              aria-expanded={controller.isGroupTypeHelpOpen}
              aria-controls="group-type-help"
              onClick={controller.toggleGroupTypeHelp}
            >
              i
            </button>
            {controller.isGroupTypeHelpOpen ? (
              <div
                id="group-type-help"
                className="groups-form-help-popover"
                role="tooltip"
              >
                <p>
                  <strong>{t("groups_type_local")}:</strong>{" "}
                  {t("groups_type_local_help")}
                </p>
                <p>
                  <strong>{t("groups_type_general")}:</strong>{" "}
                  {t("groups_type_general_help")}
                </p>
              </div>
            ) : null}
          </div>
        </div>
        <select
          id="group-type"
          value={controller.groupType}
          onChange={(event) => controller.handleGroupTypeChange(event.target.value)}
          required
        >
          <option value="" disabled>
            {t("groups_type_placeholder")}
          </option>
          <option value="local">{t("groups_type_local")}</option>
          <option value="general">{t("groups_type_general")}</option>
        </select>
      </div>

      {controller.groupType === "local" ? (
        <div className="groups-form-row">
          <label htmlFor="group-location">{t("groups_location")}</label>
          <div className="groups-location-field" ref={controller.groupLocationFieldRef}>
            <input
              id="group-location"
              type="text"
              value={controller.groupLocation}
              onChange={(event) =>
                controller.handleGroupLocationChange(event.target.value)
              }
              placeholder={t("groups_location_placeholder")}
              autoComplete="off"
              required={controller.groupType === "local"}
            />
            {controller.groupLocationSuggestions.length > 0 ? (
              <ul className="groups-location-suggestions" role="listbox">
                {controller.groupLocationSuggestions.map((option) => {
                  const optionKey =
                    typeof option === "string"
                      ? option
                      : `${option.label || option.name || "place"}-${
                          option.type || ""
                        }-${option.lat ?? ""}-${option.lng ?? ""}`;
                  const optionLabel =
                    typeof option === "string"
                      ? option
                      : option.label || option.name || "";

                  return (
                    <li key={optionKey}>
                      <button
                        type="button"
                        className="groups-location-suggestion"
                        onClick={() =>
                          controller.selectGroupLocationSuggestion(option)
                        }
                      >
                        {optionLabel}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="groups-form-row">
        <label htmlFor="group-water-type">
          {t("groups_water_type", { defaultValue: "Water type" })}
        </label>
        <select
          id="group-water-type"
          value={controller.groupWaterType}
          onChange={(event) => controller.setGroupWaterType(event.target.value)}
        >
          {GROUP_WATER_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.value === "all"
                ? t("all", { defaultValue: option.defaultLabel })
                : t(`groups_water_type_${option.value}`, {
                    defaultValue: option.defaultLabel,
                  })}
            </option>
          ))}
        </select>
      </div>

      <div className="groups-form-row">
        <label htmlFor="group-way-of-fishing">
          {t("catch_way_of_fishing", { defaultValue: "Way of Fishing" })}
        </label>
        <select
          id="group-way-of-fishing"
          value={controller.groupWayOfFishing}
          onChange={(event) =>
            controller.handleGroupWayOfFishingChange(event.target.value)
          }
        >
          <option value="all">{t("all", { defaultValue: "All" })}</option>
          {WAY_OF_FISHING_OPTIONS.map((way) => (
            <option key={way} value={way}>
              {way === "Shore"
                ? t("fishing_way_shore", { defaultValue: way })
                : way === "Boat"
                  ? t("fishing_way_boat", { defaultValue: way })
                  : way}
            </option>
          ))}
        </select>
      </div>

      <div className="groups-form-row">
        <label htmlFor="group-type-of-fishing">
          {t("catch_type_of_fishing", { defaultValue: "Type of Fishing" })}
        </label>
        <select
          id="group-type-of-fishing"
          value={controller.groupTypeOfFishing}
          onChange={(event) => controller.setGroupTypeOfFishing(event.target.value)}
        >
          <option value="all">{t("all", { defaultValue: "All" })}</option>
          {controller.groupTypeOfFishingOptions.map((type) => (
            <option key={type} value={type}>
              {getGroupTypeOfFishingLabel(t, type)}
            </option>
          ))}
        </select>
      </div>

      <div className="groups-form-row">
        <div className="groups-form-label-row">
          <label htmlFor="group-visibility">{t("groups_visibility")}</label>
          <div className="groups-form-help" ref={controller.visibilityHelpRef}>
            <button
              type="button"
              className="groups-form-help-trigger"
              aria-label={t("groups_visibility_help_button")}
              aria-expanded={controller.isVisibilityHelpOpen}
              aria-controls="group-visibility-help"
              onClick={controller.toggleVisibilityHelp}
            >
              i
            </button>
            {controller.isVisibilityHelpOpen ? (
              <div
                id="group-visibility-help"
                className="groups-form-help-popover"
                role="tooltip"
              >
                <p>
                  <strong>{t("groups_public")}:</strong>{" "}
                  {t("groups_public_visibility_help")}
                </p>
                <p>
                  <strong>{t("groups_private")}:</strong>{" "}
                  {t("groups_private_visibility_help")}
                </p>
              </div>
            ) : null}
          </div>
        </div>
        <select
          id="group-visibility"
          value={controller.visibility}
          onChange={(event) => controller.setVisibility(event.target.value)}
        >
          <option value="public">{t("groups_public")}</option>
          <option value="private">{t("groups_private")}</option>
        </select>
      </div>

      <div className="groups-form-row">
        <label htmlFor="group-posting-approval-mode">
          {t("groups_posting_approval_label", {
            defaultValue: "Posting approval",
          })}
        </label>
        <select
          id="group-posting-approval-mode"
          value={controller.postingApprovalMode}
          onChange={(event) =>
            controller.setPostingApprovalMode(event.target.value)
          }
        >
          {GROUP_POSTING_APPROVAL_MODE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(`groups_posting_approval_${option.value}`, {
                defaultValue: option.defaultLabel,
              })}
            </option>
          ))}
        </select>
      </div>

      {controller.createError ? (
        <p className="groups-form-error">{controller.createError}</p>
      ) : null}

      <div className="groups-form-actions">
        <button
          type="button"
          className="groups-secondary-btn"
          onClick={controller.cancelCreateGroup}
          disabled={controller.isCreating}
        >
          {t("cancel")}
        </button>
        <button
          type="submit"
          className="groups-primary-btn"
          disabled={controller.isCreating}
        >
          {controller.isCreating ? t("groups_creating") : t("groups_create_group")}
        </button>
      </div>
    </form>
  );
}
